import React, { useCallback, useEffect, useId } from 'react';
import { useSelector } from 'react-redux';
import { useSearch } from 'shared/hooks/search-v2.hooks';
import { useRevealedCount } from 'shared/hooks/search-results-paging.hooks';
import { getPickerHits } from 'shared/utils/search-v2-mapper';
import { colorPalette, SEARCH_TYPES_KEYS, SEARCH_V2_PICKER_PAGING } from 'shared/constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import styles from './select-content.module.scss';
import { TextInput } from 'src/components/design-system/forms';
import { Button, SearchBox } from 'src/components/design-system';
import { Divider } from 'antd';
import { KnowledgeItemRow } from '../knowledge';
import { LabItemRow } from '../lab';
import { ChecklistItemRow } from '../admission';
import { CategoryItemRow } from './category-item-row.component';
import * as Yup from 'yup';
import { NotFound } from 'src/components/common';
import { Loader } from '../loader/Loader.component';
import { useKnowledgeParser } from 'shared/utils/rotations';
import { formatNumber } from 'shared/utils';
import { ListItemsNavigator } from 'src/components/common/items-navigator/list';
import { useLiveRegion } from 'src/hooks/accessibility/use-live-region';

const url = Yup.string().url().required('Required!');

/**
 * Resource picker on search v2, or a pasted URL as an external resource. Hits show
 * `SEARCH_V2_PICKER_PAGING.INITIAL` at a time behind a "Show more" link; a new search
 * starts over.
 *
 * @param {object} props
 * @param {string[]} props.disabledIds - Ids already added; their rows are disabled.
 * @param {boolean} [props.loading] - Disables the buttons while the parent saves.
 * @param {object} props.lang - `CONTENT_SELECTOR` strings.
 * @param {Function} [props.onCancel] - Called on Close.
 * @param {Function} [props.onSubmit] - Called with the composed resource to add.
 * @param {object} props.knowledge - The knowledge slice.
 * @param {object} props.admission - The admission slice.
 * @param {object} props.lab - The lab slice.
 * @returns {JSX.Element}
 * @example
 * <SelectContent disabledIds={ids} onSubmit={add} onCancel={close} />
 */
const SelectContentComponent = ({
  disabledIds,
  loading,
  lang,
  onCancel = () => {},
  onSubmit = () => {},
  knowledge,
  admission,
  lab,
}) => {
  const [selected, setSelected] = React.useState({});
  const [filter, setFilter] = React.useState('');
  const [term, setTerm] = React.useState('');
  const [newTitle, setNewTitle] = React.useState('');
  // Counts submits: a re-submitted term refetches, and an unchanged answer keeps its
  // identity, so the data alone cannot restart the paging.
  const [submissions, setSubmissions] = React.useState(0);
  const searchId = useId();
  const isUrl = url.isValidSync(filter);
  const { data: searchResults, dataUpdatedAt, isError, isFetching, refetch } = useSearch(term);
  const results = React.useMemo(() => getPickerHits(searchResults), [searchResults]);
  const searchLang = useSelector(getLang('SEARCH_SCREEN'));
  const { visibleCount, remaining, nextBatch, showMore } = useRevealedCount({
    total: results.length,
    resetKey: submissions,
    initial: SEARCH_V2_PICKER_PAGING.INITIAL,
    step: SEARCH_V2_PICKER_PAGING.STEP,
  });
  const { announce, liveRegion } = useLiveRegion();
  const isSelected = React.useCallback(
    ({ id, workspaceId }) => selected.id === id && selected.workspaceId === workspaceId,
    [selected],
  );
  const isSelectActive = React.useCallback(({ id }) => !!selected?.id, [selected]);
  const { compose } = useKnowledgeParser({ knowledge, admission, lab });
  const isDisabled = React.useCallback(id => disabledIds.includes(id), [disabledIds]);

  let notFound = lang.NO_RESULTS.replace('{term}', term);
  if (isUrl) notFound = lang.ADD_EXTERNAL_URL.replace('{url}', filter);
  else if (!term) notFound = lang.START_TYPING;
  else if (isError) notFound = lang.ERROR_OCCURRED;

  useEffect(() => {
    if (!dataUpdatedAt || !term) return;
    announce(
      results.length > 0
        ? lang.RESULTS_COUNT?.replace('{count}', formatNumber(results.length)) ||
            `${formatNumber(results.length)} results found`
        : lang.NO_RESULTS_ANNOUNCEMENT || 'No results found',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- announce once per response, not when results or term change
  }, [dataUpdatedAt]);

  const handleFilterChange = useCallback(e => {
    const { value } = e.target;
    setFilter(value);
    if (url.isValidSync(value)) {
      setSelected({});
      setTerm('');
      setNewTitle(value);
    }
  }, []);

  // Searching the same term again asks the server again.
  const handleSearch = useCallback(
    value => {
      if (url.isValidSync(value)) return;
      const next = value.trim();
      setSelected({});
      setNewTitle('');
      setSubmissions(count => count + 1);
      if (next && next === term) refetch();
      else setTerm(next);
    },
    [term, refetch],
  );

  const handleSubmit = useCallback(async () => {
    const itemToAdd = { ...selected, title: newTitle, url: selected?.id ? undefined : filter };
    onSubmit(compose(itemToAdd));
    setSelected({});
    setNewTitle('');
    setFilter('');
    setTerm('');
  }, [onSubmit, selected, newTitle, filter, compose]);

  const handleSelect = useCallback(item => {
    setSelected(item);
    setNewTitle(item.title);
  }, []);

  const clearSelected = useCallback(() => {
    setSelected({});
    setNewTitle('');
  }, []);

  const onShowMore = () => {
    showMore();
    announce(
      searchLang.MORE_RESULTS_SHOWN.replace('{number}', formatNumber(nextBatch)).replace(
        '{remaining}',
        formatNumber(remaining - nextBatch),
      ),
    );
  };

  const renderByType = React.useMemo(
    () => ({
      [SEARCH_TYPES_KEYS.documents]: ({ item }, i) => (
        <KnowledgeItemRow
          key={i}
          item={item}
          onSelect={handleSelect}
          disabled={isDisabled(item.id)}
          selected={isSelected(item)}
          isHidden={isSelectActive(item) && !isSelected(item)}
        />
      ),
      [SEARCH_TYPES_KEYS.compendium]: ({ item }, i) => (
        <LabItemRow
          key={i}
          item={item}
          onSelect={handleSelect}
          disabled={isDisabled(item.id)}
          selected={isSelected(item)}
          isHidden={isSelectActive(item) && !isSelected(item)}
        />
      ),
      [SEARCH_TYPES_KEYS.admission]: ({ item }, i) => (
        <ChecklistItemRow
          key={i}
          item={item}
          onSelect={handleSelect}
          disabled={isDisabled(item.id)}
          selected={isSelected(item)}
          isHidden={isSelectActive(item) && !isSelected(item)}
        />
      ),
      [SEARCH_TYPES_KEYS.category]: ({ item, type }, i) => (
        <CategoryItemRow
          key={i}
          item={item}
          type={type}
          onSelect={handleSelect}
          disabled={isDisabled(item.id)}
          selected={isSelected(item)}
          isHidden={isSelectActive(item) && !isSelected(item)}
        />
      ),
    }),

    [isSelected, handleSelect, isDisabled, isSelectActive],
  );

  return (
    <div className="new-design-system">
      {liveRegion}
      <Divider dashed style={{ borderColor: colorPalette.neutral5, margin: '15px 0' }} />
      <div className={styles.search}>
        <label htmlFor={searchId}>{lang.SEARCH_OR_PASTE}</label>
        <SearchBox
          id={searchId}
          size="large"
          value={filter}
          onChange={handleFilterChange}
          onSearch={handleSearch}
          enterButton={lang.SEARCH}
          loading={isFetching}
        />
      </div>
      <TextInput
        label={lang.ITEM_TITLE}
        onChange={e => setNewTitle(e.target.value)}
        value={newTitle}
        className="new-design-system"
        allowClear
      />
      <div className={styles.results}>
        {results.length > 0 ? (
          <>
            <ListItemsNavigator className={styles.resultsList}>
              {results
                .slice(0, visibleCount)
                .map(({ type, item }, i) => renderByType[type]?.({ item, type }, i))}
            </ListItemsNavigator>
            {remaining > 0 && !selected.id && (
              <Button type="link" onClick={onShowMore}>
                {searchLang.SHOW_MORE_RESULTS.replace('{number}', formatNumber(nextBatch))}
              </Button>
            )}
            {selected.id && (
              <Button className={styles.clearSelected} onClick={clearSelected}>
                {lang.CLEAR_SELECTED}
              </Button>
            )}
          </>
        ) : isFetching ? (
          <Loader />
        ) : (
          <NotFound text={notFound} className={styles.notFound} />
        )}
      </div>
      <Divider dashed style={{ borderColor: colorPalette.neutral5, margin: '15px 0' }} />
      <div className={styles.buttons}>
        <Button onClick={onCancel} disabled={loading}>
          {lang.CANCEL}
        </Button>
        <Button type="primary" onClick={handleSubmit} disabled={!newTitle} loading={loading}>
          {lang.SUBMIT}
        </Button>
      </div>
    </div>
  );
};

export { SelectContentComponent };
